import React, {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { css, styled } from "goober";
import { Modal } from "clutch/src/Modal/Modal.jsx";
import { cssDialog } from "clutch/src/Modal/styles.mjs";
import { tabletLarge } from "clutch/src/Style/style.mjs";

import CloseIcon from "@/inline-assets/close.svg";
import { classNames } from "@/util/class-names.mjs";
import getRootStyleProperty from "@/util/get-root-style-property.mjs";
import globals from "@/util/global-whitelist.mjs";
import useForwardedRef from "@/util/use-forwarded-ref.mjs";

// Reads the animation duration from the stylesheet and returns in milliseconds
export const getAnimationDuration = () => {
  const [duration] = getRootStyleProperty("--transition").split(" ") ?? [""];
  const mod = duration.endsWith("ms") ? 1 : duration.endsWith("s") ? 1000 : 0;
  return mod * parseFloat(duration);
};

const CLOSED_CLASS = "modal-closed";
const OPEN_CLASS = "modal-open";

type FullModalProps = {
  buttonCollapse?: { size?: number; side?: number; top?: number };
  // Return false to prevent the modal from closing, for example if the router will trigger this
  onModalClose?: () => boolean | void;
  onModalShow?: () => void;
  initialShow?: boolean;
  fullHeight?: boolean;
};

export interface FullModalRefOptions extends HTMLDialogElement {
  showModal: () => void;
  close: () => void;
}

function FullModal(
  {
    buttonCollapse = {},
    onModalClose: onModalCloseProp,
    onModalShow: onModalShowProp,
    initialShow = false,
    children,
    className,
    fullHeight,
    ...restProps
  }: React.ComponentPropsWithRef<"dialog"> & FullModalProps,
  forwardedRef: React.ForwardedRef<FullModalRefOptions>,
) {
  const ref = useForwardedRef<HTMLDialogElement>(forwardedRef);

  // set transition because we move the node along the tree
  const [transitionClass, setTransitionClass] = useState(
    initialShow ? OPEN_CLASS : CLOSED_CLASS,
  );
  const [show, setShow] = useState(false);

  const onModalShow = useCallback(() => {
    setShow(true);
    onModalShowProp?.();
  }, [onModalShowProp]);

  const onModalClose = useCallback(() => {
    const shouldClose = onModalCloseProp?.() ?? true;
    if (shouldClose) {
      setShow(false);
    }
  }, [onModalCloseProp]);

  useLayoutEffect(() => {
    if (!show) {
      setTransitionClass(CLOSED_CLASS);
      return;
    }
    setTransitionClass(OPEN_CLASS);
  }, [show]);

  useLayoutEffect(() => {
    if (!ref.current) return;
    // for cleanup
    const timeouts: Timeout[] = [];
    const frames: number[] = [];

    const obs = new MutationObserver((mutations) => {
      for (const { removedNodes } of mutations) {
        // If our container is removed we want to animate it out
        // so we add it back to the tree to play the animation then remove it after completion
        for (const node of Array.from(removedNodes)) {
          if (!(node instanceof Element)) continue;
          // we have to attach a new element because clutch Modal won't render its children when !show
          // we do not have position-fixed so we will attach to a new 0 size element
          if (!ref.current) return;
          const app_container = ref.current.parentElement!; // assume ref is in document
          const new_parent = globals.document.createElement("dialog");

          new_parent.classList.add(cssDialog(), cssFixedModal(), "closing");
          if (className) new_parent.classList.add(className);

          new_parent.appendChild(node);
          app_container.appendChild(new_parent);

          new_parent.showModal();

          const transitionElements = [node, new_parent];
          transitionElements.forEach((el) => {
            el.classList.remove(CLOSED_CLASS);
            el.classList.add(OPEN_CLASS);
          });

          // we have to delay adding closed animation because the element moved in the tree,
          // to start the animation it has to first have no animated class then add next tick
          frames.push(
            requestAnimationFrame(() => {
              transitionElements.forEach((el) => {
                el.classList.remove(OPEN_CLASS);
                el.classList.add(CLOSED_CLASS);
              });

              // we have exposed transition duration from styles
              // so we know exactly how long we have to display this animation before it is invisible
              timeouts.push(
                setTimeout(() => new_parent.remove(), getAnimationDuration()),
              );
            }),
          );
        }

        // for open attribute we animate the inner based on open state
        // note that we don't have to check this value changed to conditionally set state;
        // if this is "open" we know its value changed
      }
    });
    obs.observe(ref.current, { attributeFilter: ["open"], childList: true });

    return () => {
      obs.disconnect();
      timeouts.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
    };
  }, [className, ref]);

  const handleClose = useCallback(() => {
    ref.current?.close();
  }, [ref]);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const closeOnClick = useCallback<React.MouseEventHandler>(
    (e) => {
      if (!ref.current) return;
      if (e.currentTarget === e.target) {
        ref.current.close();
      }
    },
    [ref],
  );

  return (
    <Modal
      ref={ref}
      {...classNames(
        className,
        cssFixedModal(),
        transitionClass,
        transitionClass === OPEN_CLASS && cssBackdrop(),
      )}
      {...{ onModalClose, onModalShow, ...restProps }}
    >
      <ModalContainer
        {...classNames(
          "flex align-center",
          "overflow-hidden", // NEED THIS FOR CLOSE ANIMATION W/O FIXED POSITION
          transitionClass,
        )}
      >
        <ScrollContainer
          ref={scrollContainerRef}
          className="scroll-container side-margin-auto"
          onClick={closeOnClick}
        >
          <div ref={contentRef} className="modal-view">
            {children}
          </div>
          <ButtonFormWrapper>
            <ModalClose
              className="modal-close-button"
              onClick={handleClose}
              {...{ buttonCollapse, fullHeight }}
            />
          </ButtonFormWrapper>
        </ScrollContainer>
      </ModalContainer>
    </Modal>
  );
}

const ModalClose = ({
  onClick,
  buttonCollapse: { size = 12, side = 4, top = 4 },
  fullHeight = false,
  className,
}: {
  onClick: React.MouseEventHandler;
  buttonCollapse: { size?: number; side?: number; top?: number };
  fullHeight?: boolean;
  className?: string;
}) => {
  // We cannot select &:hover to animate in css because we are moving the parent component
  // in the DOM tree, I think the animation is reset when this happens
  const [isHovered, setIsHovered] = useState(false);
  const handleHover = useCallback(() => setIsHovered(true), []);
  const handleUnhover = useCallback(() => setIsHovered(false), []);

  return (
    <ModalCloseButton
      {...classNames(
        className,
        css`
          --collapsed-button-size: calc(var(--sp-1) * ${size});
          --collapsed-button-side-margin: calc(var(--sp-1) * ${side});
          --collapsed-button-top-margin: calc(var(--sp-1) * ${top});
        `,
        isHovered && "is-hovered",
      )}
      type="reset" // for dialog behavior
      onClick={onClick}
      onMouseEnter={handleHover}
      onMouseLeave={handleUnhover}
      $fullHeight={fullHeight}
    >
      <div className="modal-close-circle wh-full round" />
      <div className="modal-close-icon-wrapper">
        <CloseIcon />
      </div>
    </ModalCloseButton>
  );
};

const ButtonFormWrapper = styled("form")`
  position: relative;
  width: 0;
`;

const cssFixedModal = () => css`
  left: 0;
  right: 0;
  bottom: 0;

  height: 100vh;
  width: 100vw;

  overflow: hidden;

  /* override dialog styles */
  max-width: none;
  max-height: none;
  margin-bottom: 0;

  &.notification-visible {
    /* Match notification height */
    height: calc(100vh - var(--sp-8));
  }

  pointer-events: none !important;
  > * {
    pointer-events: auto;
  }
  clip-path: inset(var(--title-bar-height) 0 0 0);

  &.${CLOSED_CLASS} {
    &::backdrop {
      background-color: transparent;
      /* Prefer NOT animating the filter because css is buggy with edges */
      opacity: 0;
    }
  }

  /* When we are closing we want to show ABOVE all other elements */
  &.closing,
  &.closing::backdrop {
    z-index: 10;
    pointer-events: none !important;
    * {
      pointer-events: none !important;
    }
    display: block;
  }
`;

const cssBackdrop = () => css`
  &::backdrop {
    transition: ${getRootStyleProperty("--transition")};
    transition-property: background-color, opacity;
    background-color: ${getRootStyleProperty("--shade10-75")} !important;
    backdrop-filter: blur(16px);
  }
`;

const ScrollContainer = styled("div", forwardRef)`
  position: absolute;
  left: 0;
  right: 0;

  cursor: pointer;
  > * {
    cursor: auto;
  }

  display: flex;
  justify-content: center;

  max-height: 100%;

  overflow-y: auto;
  overflow-y: overlay;

  @media (prefers-reduced-motion: no-preference) {
    scroll-behavior: smooth;
  }

  > .modal-view {
    height: fit-content;
    min-width: 0;
  }
`;

const ModalContainer = styled("div")`
  top: 3px;
  bottom: 0;
  /* Must use left right instead of width for mutation animation :))) */
  left: 0;
  right: 0;

  height: 100%;

  &.${CLOSED_CLASS} {
    .scroll-container {
      transform: translateY(100vh);

      /* very fun: clients force inputs to be visible at all times, taking precedence over transform  */
      /* this somehow fixes it and doesn't even change how they are displayed!! */
      input:focus {
        transform: translateY(-100vh);
      }
    }
    .modal-close-button {
      opacity: 0;
    }
  }

  .scroll-container {
    transition: var(--transition);
    transition-property: opacity, transform, margin;
  }

  & {
    pointer-events: none;
    > * {
      pointer-events: auto;
    }
  }
`;

export const ModalCloseButton = styled("button")<{
  $fullHeight: boolean;
}>`
  --button-size: var(--sp-14);
  --button-side-margin: var(--sp-6);
  --button-top-margin: ${(p) =>
    p.$fullHeight ? "var(--sp-16)" : "var(--sp-0)"};

  --transform: translateX(var(--button-side-margin));

  width: var(--button-size);
  height: var(--button-size);

  position: sticky;
  top: var(--button-top-margin);
  transform: var(--transform);
  z-index: 2;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  background-color: transparent;

  transition: var(--transition);
  transition-property: transform, top, width, height;

  .modal-close-circle {
    position: absolute;
    background: var(--shade9-75);
    border: solid var(--sp-0_5) currentColor;

    transition: var(--transition);
    transition-property: color, transform;
    color: var(--shade5);
  }

  ${tabletLarge} {
    --button-size: var(--collapsed-button-size);
    --button-top-margin: var(--collapsed-button-top-margin);
    --button-side-margin: calc(
      -1 * var(--collapsed-button-side-margin) - var(--collapsed-button-size)
    );

    .modal-close-circle {
      background: var(--shade9);
    }
  }

  &.is-hovered .modal-close-circle {
    color: var(--shade3);
    transform: scale(90%);
  }

  .modal-close-icon-wrapper {
    position: absolute;
    width: var(--sp-6);
    transition: color var(--transition);
    color: var(--shade2);
  }
  &.is-hovered .modal-close-icon-wrapper {
    color: var(--shade0);
  }

  &.is-hovered:active {
    transform: var(--transform) scale(90%);
    .modal-close-circle {
      color: var(--shade2);
    }
  }
`;

export default forwardRef(FullModal);
